import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { teamApi, goalsApi, reviewsApi, competenciesApi, performanceCycleApi } from '../lib/api';
import { hasAllKpiComments, hasAllKpiRatings } from '../lib/reviewGates';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Loader2, CheckCircle, XCircle, Users, ArrowLeft,
  Clock, AlertCircle, Target, FileText, Award, UserCheck, Send,
  Briefcase, ChevronDown, ChevronUp, Flag, Star, TrendingUp,
  Eye, Download, Info, BarChart3, PenTool, Lock, Circle, Save
} from 'lucide-react';

// Progress Tracker Component for Review Sections
const SectionProgressTracker = ({ sections }) => {
  const completedCount = sections.filter(s => s.completed).length;
  const totalCount = sections.length;
  
  return (
    <div className="flex items-center gap-4 flex-wrap py-2">
      {sections.map((section, index) => (
        <div key={index} className="flex items-center gap-1.5">
          {section.completed ? (
            <CheckCircle className="h-4 w-4 text-emerald-500" />
          ) : (
            <Circle className="h-4 w-4 text-gray-300" />
          )}
          <span className={`text-xs ${section.completed ? 'text-emerald-700 font-medium' : 'text-gray-500'}`}>
            {section.name}
          </span>
        </div>
      ))}
      <Badge variant="outline" className={`ml-auto text-xs ${completedCount === totalCount ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
        {completedCount}/{totalCount} Complete
      </Badge>
    </div>
  );
};

// New status configuration for Goal Setting, Mid-Year Review, Year-End Review
const reviewStageStatusConfig = {
  not_due: { label: 'Not Due', color: 'bg-gray-200 text-gray-600', icon: AlertCircle },
  pending_submission: { label: 'Pending Submission', color: 'bg-amber-100 text-amber-700', icon: Clock },
  pending_manager: { label: 'Pending Manager', color: 'bg-blue-100 text-blue-700', icon: Send },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

// Status configuration for Calibration
const calibrationStatusConfig = {
  not_due: { label: 'Not Due', color: 'bg-gray-200 text-gray-600', icon: AlertCircle },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

const goalStatusColors = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

// A goal the manager sends back returns to "draft" but keeps manager_feedback until the
// employee resubmits. Legacy goals may still carry the old "rejected" status. Either case
// means it has been rejected and is awaiting the employee's revision.
const needsRevision = (g) => g.status === 'rejected' || (g.status === 'draft' && !!g.manager_feedback);

// Inline Goal Card Component for Manager View
const GoalCard = ({ goal, expanded, onToggle }) => {
  return (
    <div className="p-3 rounded-lg border bg-white">
      <div 
        className="flex items-start justify-between cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {goal.goal_type === 'business' ? 
              <Briefcase className="h-3.5 w-3.5 text-blue-600" /> : 
              <Users className="h-3.5 w-3.5 text-purple-600" />
            }
            <span className="font-medium text-sm">{goal.title}</span>
            <Badge variant="outline" className="text-xs">{goal.weight}%</Badge>
            <Badge variant="outline" className="text-xs">
              {goal.goal_type === 'business' ? 'Business' : 'People'}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground ml-6">
            {goal.kpis?.length || 0} KPIs
          </div>
        </div>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>
      
      {/* Expanded KPIs Table */}
      {expanded && goal.kpis && goal.kpis.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left p-2 font-medium text-muted-foreground border-b">KPI</th>
                <th className="text-left p-2 font-medium text-muted-foreground border-b">Measurement</th>
                <th className="text-left p-2 font-medium text-muted-foreground border-b">Deadline</th>
                <th className="text-right p-2 font-medium text-muted-foreground border-b">Weight</th>
              </tr>
            </thead>
            <tbody>
              {goal.kpis.map((kpi, index) => (
                <tr key={kpi.id || index} className="border-b border-slate-100 last:border-0">
                  <td className="p-2 text-slate-700">{kpi.name}</td>
                  <td className="p-2 text-slate-600">{kpi.measurement}</td>
                  <td className="p-2 text-slate-600">{kpi.timeline}</td>
                  <td className="p-2 text-right font-medium text-slate-700">{kpi.weight}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Goal Details Card for Reviews - Always shows KPIs with optional ratings
const ReviewGoalCard = ({ goal, comment, showRatings, kpiRatings, onKpiRatingChange, showComment = true }) => {
  return (
    <div className="border rounded-lg bg-white overflow-hidden">
      {/* Goal Header */}
      <div className="bg-slate-50 px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          {goal.goal_type === 'business' ? 
            <Briefcase className="h-4 w-4 text-blue-600" /> : 
            <Users className="h-4 w-4 text-purple-600" />
          }
          <span className="font-medium">{goal.title}</span>
          <Badge variant="outline" className="text-xs ml-auto">{goal.weight}%</Badge>
          <Badge variant="outline" className="text-xs">
            {goal.goal_type === 'business' ? 'Business' : 'People'}
          </Badge>
        </div>
      </div>
      
      {/* KPIs Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">KPI</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">Measurement</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">Deadline</th>
              <th className="text-right px-4 py-2 font-medium text-muted-foreground">Weight</th>
              {showRatings && (
                <th className="text-center px-4 py-2 font-medium text-muted-foreground">Rating</th>
              )}
            </tr>
          </thead>
          <tbody>
            {goal.kpis?.map((kpi, index) => (
              <tr key={kpi.id || index} className="border-b last:border-0 hover:bg-gray-50/50">
                <td className="px-4 py-3 text-slate-800">{kpi.name}</td>
                <td className="px-4 py-3 text-slate-600">{kpi.measurement}</td>
                <td className="px-4 py-3 text-slate-600">{kpi.timeline}</td>
                <td className="px-4 py-3 text-right font-medium text-slate-700">{kpi.weight}%</td>
                {showRatings && (
                  <td className="px-4 py-3 text-center">
                    {onKpiRatingChange ? (
                      <SearchableSelect
                        value={kpiRatings?.[kpi.id]?.toString() || ''}
                        onValueChange={(value) => onKpiRatingChange(kpi.id, parseInt(value))}
                        placeholder="Rate..."
                        className="w-28 h-8 text-xs mx-auto"
                        options={[
                          { value: "1", label: "1 - Poor" },
                          { value: "2", label: "2 - Below" },
                          { value: "3", label: "3 - Meets" },
                          { value: "4", label: "4 - Exceeds" },
                          { value: "5", label: "5 - Outstanding" },
                        ]}
                      />
                    ) : (
                      <Badge className={`text-xs ${
                        kpi.rating >= 4 ? 'bg-green-100 text-green-700' :
                        kpi.rating === 3 ? 'bg-blue-100 text-blue-700' :
                        kpi.rating >= 1 ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {kpi.rating ? `${kpi.rating}/5` : '-'}
                      </Badge>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Comment Section */}
      {showComment && comment && (
        <div className="px-4 py-3 bg-slate-50 border-t">
          <div className="text-xs text-muted-foreground mb-1">Comment:</div>
          <div className="text-sm text-slate-700">{comment}</div>
        </div>
      )}
    </div>
  );
};

// Stage Status Badge Component for Goal Setting, Mid-Year, Year-End
const ReviewStageStatusBadge = ({ status }) => {
  const config = reviewStageStatusConfig[status] || reviewStageStatusConfig.not_due;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} flex items-center gap-1 text-xs`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Stage Status Badge Component for Calibration
const CalibrationStatusBadge = ({ status }) => {
  const config = calibrationStatusConfig[status] || calibrationStatusConfig.not_due;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} flex items-center gap-1 text-xs`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

export default function TeamGoalsPage() {
  const { user, isAdmin, isHR } = useAuth();
  const [teamStatus, setTeamStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberGoals, setMemberGoals] = useState([]);
  const [memberReview, setMemberReview] = useState(null);
  const [competencies, setCompetencies] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [rejectionFeedback, setRejectionFeedback] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  // Rejection of an employee's submitted mid-year/year-end KPI comments (mirrors goal rejection above)
  const [commentRejectionFeedback, setCommentRejectionFeedback] = useState('');
  const [showCommentRejectForm, setShowCommentRejectForm] = useState(null); // 'mid_year' | 'year_end' | null
  const [submitting, setSubmitting] = useState(false);
  const [performanceCycle, setPerformanceCycle] = useState(null);
  const [expandedGoals, setExpandedGoals] = useState({});
  
  // Manager review feedback state
  const [midYearManagerComments, setMidYearManagerComments] = useState({});
  const [yearEndManagerComments, setYearEndManagerComments] = useState({});
  const [kpiRatings, setKpiRatings] = useState({}); // Changed to KPI-level ratings: { kpi_id: rating }
  
  // Mid-Year Feedback state (general, not per goal)
  const [midYearFeedback, setMidYearFeedback] = useState({
    what_went_well: '',
    what_could_be_better: '',
    employee_strengths: '',
    employee_opportunities: ''
  });
  
  // Career Development state (general, not per goal)
  const [careerDevelopment, setCareerDevelopment] = useState('');
  
  // Competency ratings state
  const [competencyRatings, setCompetencyRatings] = useState({});
  const [competencyFeedback, setCompetencyFeedback] = useState({});
  
  // Scorecard modal state
  const [showScorecardModal, setShowScorecardModal] = useState(false);
  const [scorecardMember, setScorecardMember] = useState(null);
  const [scorecardGoals, setScorecardGoals] = useState([]);
  const [scorecardReview, setScorecardReview] = useState(null);
  const [loadingScorecard, setLoadingScorecard] = useState(false);
  
  // Calibration signature state
  const [signingAs, setSigningAs] = useState(null); // 'manager' or 'employee'
  const [signatureName, setSignatureName] = useState('');
  const [signingLoading, setSigningLoading] = useState(false);

  // Mid-Year Review workflow state for managers
  const [midYearReviewStatus, setMidYearReviewStatus] = useState('not_started'); // 'not_started' | 'in_progress' | 'submitted'
  const [midYearReviewSubmitting, setMidYearReviewSubmitting] = useState(false);
  const [showMidYearReviewForm, setShowMidYearReviewForm] = useState(false);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadTeamStatus();
    loadCompetencies();
    loadPerformanceCycle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTeamStatus = async () => {
    try {
      const response = await teamApi.getStatusOverview(currentYear);
      setTeamStatus(response.data.team);
    } catch (error) {
      console.error('Failed to load team status:', error);
      toast.error('Failed to load team status');
    } finally {
      setLoading(false);
    }
  };

  const loadCompetencies = async () => {
    try {
      const response = await competenciesApi.getAll();
      setCompetencies(response.data);
    } catch (error) {
      console.error('Failed to load competencies:', error);
    }
  };

  const loadPerformanceCycle = async () => {
    try {
      const response = await performanceCycleApi.get(currentYear);
      setPerformanceCycle(response.data);
    } catch (error) {
      console.error('Failed to load performance cycle:', error);
    }
  };

  // Helper function to format date as "MMM DD"
  const formatStageDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return '';
    }
  };

  const loadMemberDetails = async (member) => {
    setLoadingDetails(true);
    setSelectedMember(member);
    setShowRejectForm(false);
    setRejectionFeedback('');
    setShowCommentRejectForm(null);
    setCommentRejectionFeedback('');
    setExpandedGoals({});
    setShowMidYearReviewForm(false);
    try {
      const [goalsRes, reviewsRes] = await Promise.all([
        goalsApi.getAll({ employee_id: member.id, year: currentYear }),
        reviewsApi.getAll({ employee_id: member.id, year: currentYear })
      ]);
      setMemberGoals(goalsRes.data);
      const reviewData = reviewsRes.data[0] || null;
      setMemberReview(reviewData);

      // Initialize manager's mid-year review status and pre-fill comments from any saved draft/submission
      const mgrFb = reviewData?.mid_year_feedback?.manager_feedback;
      const mgrSubStatus = reviewData?.mid_year_feedback?.manager_submission_status;
      if (mgrFb && Object.keys(mgrFb).length > 0) {
        // Pre-fill so a draft can be resumed (or a submitted review shows the manager's comments)
        setMidYearManagerComments(mgrFb);
        setMidYearReviewStatus(mgrSubStatus === 'draft' ? 'not_started' : 'submitted');
      } else {
        setMidYearManagerComments({});
        setMidYearReviewStatus('not_started');
      }
    } catch (error) {
      console.error('Failed to load member details:', error);
      toast.error('Failed to load member details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleBackToOverview = () => {
    setSelectedMember(null);
    setMemberGoals([]);
    setMemberReview(null);
    setShowRejectForm(false);
    setRejectionFeedback('');
    setShowCommentRejectForm(null);
    setCommentRejectionFeedback('');
    setExpandedGoals({});
  };

  const toggleGoalExpand = (goalId) => {
    setExpandedGoals(prev => ({ ...prev, [goalId]: !prev[goalId] }));
  };

  // Approve ALL goals for the selected member
  const handleApproveAll = async () => {
    const pendingGoals = memberGoals.filter(g => g.status === 'pending_approval');
    if (pendingGoals.length === 0) {
      toast.error('No pending goals to approve');
      return;
    }
    
    setSubmitting(true);
    try {
      // Approve all pending goals
      await Promise.all(pendingGoals.map(goal => 
        goalsApi.approve(goal.id, true, '')
      ));
      toast.success(`All ${pendingGoals.length} goals approved successfully`);
      loadMemberDetails(selectedMember);
      loadTeamStatus();
    } catch (error) {
      console.error('Failed to approve goals:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve goals');
    } finally {
      setSubmitting(false);
    }
  };

  // Reject ALL goals for the selected member
  const handleRejectAll = async () => {
    if (!rejectionFeedback.trim()) {
      toast.error('Feedback is mandatory when rejecting goals');
      return;
    }
    
    const pendingGoals = memberGoals.filter(g => g.status === 'pending_approval');
    if (pendingGoals.length === 0) {
      toast.error('No pending goals to reject');
      return;
    }
    
    setSubmitting(true);
    try {
      // Reject all pending goals with the same feedback
      await Promise.all(pendingGoals.map(goal => 
        goalsApi.approve(goal.id, false, rejectionFeedback)
      ));
      toast.success(`All ${pendingGoals.length} goals rejected. Employee will need to revise and resubmit.`);
      setShowRejectForm(false);
      setRejectionFeedback('');
      loadMemberDetails(selectedMember);
      loadTeamStatus();
    } catch (error) {
      console.error('Failed to reject goals:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject goals');
    } finally {
      setSubmitting(false);
    }
  };

  // Reject an employee's submitted KPI comments for mid-year or year-end review,
  // mirroring handleRejectAll for goals: mandatory feedback, sends it back to draft.
  const handleRejectComments = async (reviewType) => {
    if (!commentRejectionFeedback.trim()) {
      toast.error('Feedback is mandatory when rejecting comments');
      return;
    }

    setSubmitting(true);
    try {
      await reviewsApi.rejectEmployeeFeedback(selectedMember.id, currentYear, reviewType, commentRejectionFeedback);
      toast.success('Comments rejected. Employee will need to revise and resubmit.');
      setShowCommentRejectForm(null);
      setCommentRejectionFeedback('');
      loadMemberDetails(selectedMember);
      loadTeamStatus();
    } catch (error) {
      console.error('Failed to reject comments:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject comments');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit manager feedback for Mid-Year Review
  const handleSubmitMidYearFeedback = async () => {
    const allHaveComments = hasAllKpiComments(memberGoals, midYearManagerComments);

    if (!allHaveComments) {
      toast.error('Please add comments for all KPIs before submitting');
      return;
    }

    setSubmitting(true);
    try {
      // Explicit final submission → locks the manager review
      await reviewsApi.submitManagerFeedback(
        selectedMember.id,
        currentYear,
        'mid_year',
        midYearManagerComments,
        {},
        'submitted'
      );
      toast.success('Mid-Year Review comments submitted successfully');
      loadMemberDetails(selectedMember);
      loadTeamStatus();
    } catch (error) {
      console.error('Failed to submit mid-year feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  // Save partial manager mid-year comments as a DRAFT (manager can resume later, not sent as final)
  const handleSaveMidYearDraft = async () => {
    const anyFilled = Object.values(midYearManagerComments).some(v => v?.trim());
    if (!anyFilled) {
      toast.info('Add at least one comment before saving a draft.');
      return;
    }

    setSubmitting(true);
    try {
      await reviewsApi.submitManagerFeedback(
        selectedMember.id,
        currentYear,
        'mid_year',
        midYearManagerComments,
        {},
        'draft'
      );
      toast.success('Draft saved. You can come back and finish this review later.');
      loadMemberDetails(selectedMember);
      loadTeamStatus();
    } catch (error) {
      console.error('Failed to save mid-year draft:', error);
      toast.error(error.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Mid-Year General Feedback (What went well, strengths, etc.)
  const handleSubmitMidYearGeneralFeedback = async () => {
    if (!midYearFeedback.what_went_well?.trim() || !midYearFeedback.what_could_be_better?.trim() ||
        !midYearFeedback.employee_strengths?.trim() || !midYearFeedback.employee_opportunities?.trim()) {
      toast.error('Please fill all feedback fields before submitting');
      return;
    }
    
    setSubmitting(true);
    try {
      await reviewsApi.submitManagerGeneralFeedback(
        selectedMember.id,
        currentYear,
        'mid_year',
        midYearFeedback
      );
      toast.success('Mid-Year Feedback submitted successfully');
      loadMemberDetails(selectedMember);
    } catch (error) {
      console.error('Failed to submit mid-year general feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit manager feedback for Year-End Review
  const handleSubmitYearEndFeedback = async () => {
    const allHaveComments = hasAllKpiComments(memberGoals, yearEndManagerComments);
    const allKpisHaveRatings = hasAllKpiRatings(memberGoals, kpiRatings);

    if (!allHaveComments) {
      toast.error('Please add comments for all KPIs before submitting');
      return;
    }
    
    if (!allKpisHaveRatings) {
      toast.error('Please rate all KPIs before submitting');
      return;
    }
    
    setSubmitting(true);
    try {
      await reviewsApi.submitManagerFeedback(
        selectedMember.id,
        currentYear,
        'year_end',
        yearEndManagerComments,
        kpiRatings // Now sending KPI-level ratings
      );
      toast.success('Year-End Review comments and KPI ratings submitted successfully');
      loadMemberDetails(selectedMember);
      loadTeamStatus();
    } catch (error) {
      console.error('Failed to submit year-end feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Career Development feedback
  const handleSubmitCareerDevelopment = async () => {
    if (!careerDevelopment?.trim()) {
      toast.error('Please add career development notes before submitting');
      return;
    }
    
    setSubmitting(true);
    try {
      await reviewsApi.submitCareerDevelopment(
        selectedMember.id,
        currentYear,
        careerDevelopment
      );
      toast.success('Career Development notes submitted successfully');
      loadMemberDetails(selectedMember);
    } catch (error) {
      console.error('Failed to submit career development:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Competency Ratings
  const handleSubmitCompetencyRatings = async () => {
    const activeCompetencies = competencies.filter(c => c.is_active);
    const allRated = activeCompetencies.every(c => competencyRatings[c.id]);
    
    if (!allRated) {
      toast.error('Please rate all competencies before submitting');
      return;
    }
    
    setSubmitting(true);
    try {
      const ratings = activeCompetencies.map(c => ({
        competency_id: c.id,
        competency_name: c.name,
        rating: competencyRatings[c.id],
        feedback: competencyFeedback[c.id] || ''
      }));
      
      await reviewsApi.submitCompetencyRatings(
        selectedMember.id,
        currentYear,
        ratings
      );
      toast.success('Competency ratings submitted successfully');
      loadMemberDetails(selectedMember);
    } catch (error) {
      console.error('Failed to submit competency ratings:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit ratings');
    } finally {
      setSubmitting(false);
    }
  };

  // View Employee Scorecard
  const handleViewScorecard = async (member, e) => {
    e.stopPropagation(); // Prevent row click
    setLoadingScorecard(true);
    setScorecardMember(member);
    setShowScorecardModal(true);
    
    try {
      const [goalsRes, reviewsRes] = await Promise.all([
        goalsApi.getAll({ employee_id: member.id, year: currentYear }),
        reviewsApi.getAll({ employee_id: member.id, year: currentYear })
      ]);
      setScorecardGoals(goalsRes.data || []);
      setScorecardReview(reviewsRes.data?.[0] || null);
    } catch (error) {
      console.error('Failed to load scorecard data:', error);
      toast.error('Failed to load scorecard');
    } finally {
      setLoadingScorecard(false);
    }
  };

  // Rating helper functions for Scorecard
  const getRatingLabel = (rating) => {
    if (!rating) return '-';
    if (rating >= 4.6) return 'Outstanding';
    if (rating >= 3.8) return 'Exceeds Expectation';
    if (rating >= 2.7) return 'Meets Expectation';
    if (rating >= 1.7) return 'Partially Meets';
    return 'Did Not Meet';
  };

  const getRatingColor = (rating) => {
    if (!rating) return 'bg-gray-100 text-gray-600';
    if (rating >= 4.6) return 'bg-teal-100 text-teal-800';
    if (rating >= 3.8) return 'bg-green-100 text-green-800';
    if (rating >= 2.7) return 'bg-blue-100 text-blue-800';
    if (rating >= 1.7) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  // Handle signing calibration review
  const handleSignCalibration = async () => {
    if (!memberReview || !signatureName.trim()) {
      toast.error('Please enter your name to sign');
      return;
    }

    setSigningLoading(true);
    try {
      await reviewsApi.sign(memberReview.id, {
        type: signingAs,
        signature: signatureName,
      });
      toast.success('Signature recorded successfully');
      setSigningAs(null);
      setSignatureName('');
      loadMemberDetails(selectedMember);
    } catch (error) {
      console.error('Failed to sign:', error);
      toast.error('Failed to record signature');
    } finally {
      setSigningLoading(false);
    }
  };

  // Manager Mid-Year Review workflow handlers
  const handleStartManagerMidYearReview = () => {
    setMidYearReviewStatus('in_progress');
    setShowMidYearReviewForm(true);
  };

  const handleLeaveManagerMidYearReview = () => {
    setMidYearReviewStatus('not_started');
    setShowMidYearReviewForm(false);
    // Draft comments preserved for resuming
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Detail View - When a member is selected
  if (selectedMember) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="team-goals-detail">
        {/* Header with Back button */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBackToOverview} data-testid="back-to-overview-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Team Overview
          </Button>
        </div>

        {/* Member Info Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xl font-bold text-primary">
                    {selectedMember.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </span>
                </div>
                <div>
                  <CardTitle className="text-2xl">{selectedMember.name}</CardTitle>
                  <CardDescription>
                    {selectedMember.employee_number} • {selectedMember.designation || 'Employee'} • {selectedMember.department || 'N/A'}
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className="text-xs text-muted-foreground mb-1">Goal Setting</span>
                  <ReviewStageStatusBadge status={selectedMember.stages.goal_setting} />
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-xs text-muted-foreground mb-1">Mid-Year Review</span>
                  <ReviewStageStatusBadge status={selectedMember.stages.mid_year} />
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-xs text-muted-foreground mb-1">Year-End Review</span>
                  <ReviewStageStatusBadge status={selectedMember.stages.year_end} />
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-xs text-muted-foreground mb-1">Calibration</span>
                  <CalibrationStatusBadge status={selectedMember.stages.calibration} />
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {loadingDetails ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <Tabs defaultValue="goals" className="space-y-6">
            <TabsList>
              <TabsTrigger value="goals" className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                Goals ({memberGoals.length})
              </TabsTrigger>
              <TabsTrigger value="mid-year-review" className="flex items-center gap-2">
                <Flag className="h-4 w-4" />
                Mid-Year Review
              </TabsTrigger>
              <TabsTrigger value="year-end-review" className="flex items-center gap-2">
                <Award className="h-4 w-4" />
                Year-End Review
              </TabsTrigger>
              <TabsTrigger value="calibration" className="flex items-center gap-2">
                <UserCheck className="h-4 w-4" />
                Calibration
              </TabsTrigger>
            </TabsList>

            {/* Goals Tab */}
            <TabsContent value="goals">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        Goals for {currentYear}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Total Weight: {memberGoals.reduce((sum, g) => sum + (g.weight || 0), 0)}% • 
                        Business: {memberGoals.filter(g => g.goal_type === 'business').length} • 
                        People: {memberGoals.filter(g => g.goal_type === 'people').length}
                      </CardDescription>
                    </div>
                    
                    {/* Approve/Reject Buttons - Only show if there are pending goals */}
                    {memberGoals.filter(g => g.status === 'pending_approval').length > 0 && !showRejectForm && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowRejectForm(true)}
                          disabled={submitting}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="reject-all-btn"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject All
                        </Button>
                        <Button
                          onClick={handleApproveAll}
                          disabled={submitting}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="approve-all-btn"
                        >
                          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                          Approve All
                        </Button>
                      </div>
                    )}
                    
                    {/* Show status badge if all goals approved */}
                    {memberGoals.length > 0 && memberGoals.every(g => g.status === 'approved') && (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        All Goals Approved
                      </Badge>
                    )}
                    
                    {/* Show rejected badge if any goals rejected */}
                    {memberGoals.length > 0 && memberGoals.some(needsRevision) && (
                      <Badge className="bg-red-100 text-red-700">
                        <XCircle className="h-3 w-3 mr-1" />
                        Rejected - Awaiting Resubmission
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Rejection Notice with Feedback - Show when goals are rejected */}
                  {memberGoals.some(needsRevision) && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Goals have been rejected. Waiting for employee to revise and resubmit.</p>
                          {memberGoals.find(g => needsRevision(g) && g.manager_feedback)?.manager_feedback && (
                            <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-700">
                              <strong>Your Feedback:</strong> {memberGoals.find(g => needsRevision(g) && g.manager_feedback)?.manager_feedback}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Rejection Form */}
                  {showRejectForm && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <Label className="text-red-700 font-medium">Rejection Feedback (Required)</Label>
                      <p className="text-xs text-red-600 mb-2">
                        This feedback will be sent to the employee for all goals. They will need to revise and resubmit.
                      </p>
                      <Textarea
                        value={rejectionFeedback}
                        onChange={(e) => setRejectionFeedback(e.target.value)}
                        placeholder="Please provide specific feedback on what needs to be changed..."
                        className="mt-2 border-red-200"
                        rows={3}
                        data-testid="rejection-feedback-input"
                      />
                      <div className="flex gap-2 mt-3 justify-end">
                        <Button 
                          variant="outline" 
                          onClick={() => { setShowRejectForm(false); setRejectionFeedback(''); }}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={handleRejectAll}
                          disabled={submitting || !rejectionFeedback.trim()}
                          data-testid="confirm-reject-btn"
                        >
                          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                          Confirm Rejection
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {memberGoals.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      No goals found for this year
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Business Goals Section */}
                      {memberGoals.filter(g => g.goal_type === 'business').length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Briefcase className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-sm text-blue-700">Business Goals</span>
                            <span className="text-xs text-muted-foreground">
                              ({memberGoals.filter(g => g.goal_type === 'business').reduce((sum, g) => sum + (g.weight || 0), 0)}% weight)
                            </span>
                          </div>
                          <div className="space-y-2">
                            {memberGoals.filter(g => g.goal_type === 'business').map((goal) => (
                              <GoalCard 
                                key={goal.id} 
                                goal={goal} 
                                expanded={expandedGoals[goal.id]}
                                onToggle={() => toggleGoalExpand(goal.id)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* People Goals Section */}
                      {memberGoals.filter(g => g.goal_type === 'people').length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="h-4 w-4 text-purple-600" />
                            <span className="font-medium text-sm text-purple-700">People Goals</span>
                            <span className="text-xs text-muted-foreground">
                              ({memberGoals.filter(g => g.goal_type === 'people').reduce((sum, g) => sum + (g.weight || 0), 0)}% weight)
                            </span>
                          </div>
                          <div className="space-y-2">
                            {memberGoals.filter(g => g.goal_type === 'people').map((goal) => (
                              <GoalCard 
                                key={goal.id} 
                                goal={goal} 
                                expanded={expandedGoals[goal.id]}
                                onToggle={() => toggleGoalExpand(goal.id)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Mid-Year Review Tab */}
            <TabsContent value="mid-year-review">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Flag className="h-5 w-5 text-amber-600" />
                    Mid-Year Review
                  </CardTitle>
                  <CardDescription>
                    Review and provide feedback on employee&apos;s mid-year progress
                  </CardDescription>
                  {/* Progress Tracker for Mid-Year Review */}
                  {memberGoals.filter(g => g.status === 'approved').length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <SectionProgressTracker 
                        sections={[
                          {
                            name: 'Goal Comments',
                            // A draft is not "completed" — only a final manager submission counts
                            completed: memberReview?.mid_year_feedback?.manager_feedback &&
                              Object.keys(memberReview.mid_year_feedback.manager_feedback).length > 0 &&
                              memberReview?.mid_year_feedback?.manager_submission_status !== 'draft' &&
                              memberGoals.filter(g => g.status === 'approved').every(g => g.kpis?.every(kpi =>
                                memberReview?.mid_year_feedback?.manager_feedback?.[kpi.id]
                              ) ?? true)
                          },
                          {
                            name: 'Mid-Year Feedback',
                            completed: !!memberReview?.mid_year_feedback?.general_feedback?.what_went_well
                          },
                        ]}
                      />
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {memberGoals.filter(g => g.status === 'approved').length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      No approved goals to review yet
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Goals with Employee & Manager Comments Combined */}
                      <div className="space-y-4">
                        {memberGoals.filter(g => g.status === 'approved').map((goal) => (
                          <div key={goal.id} className="border rounded-lg bg-white overflow-hidden">
                            {/* Goal Header */}
                            <div className="bg-slate-50 px-4 py-3 border-b">
                              <div className="flex items-center gap-2">
                                {goal.goal_type === 'business' ? 
                                  <Briefcase className="h-4 w-4 text-blue-600" /> : 
                                  <Users className="h-4 w-4 text-purple-600" />
                                }
                                <span className="font-medium">{goal.title}</span>
                                <Badge variant="outline" className="text-xs ml-auto">{goal.weight}%</Badge>
                                <Badge variant="outline" className="text-xs">
                                  {goal.goal_type === 'business' ? 'Business' : 'People'}
                                </Badge>
                              </div>
                            </div>
                            
                            {/* KPIs Table */}
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-gray-50 border-b">
                                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">KPI</th>
                                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Measurement</th>
                                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Deadline</th>
                                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Weight</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {goal.kpis?.map((kpi, index) => (
                                    <tr key={kpi.id || index} className="border-b last:border-0 hover:bg-gray-50/50">
                                      <td className="px-4 py-3 text-slate-800">{kpi.name}</td>
                                      <td className="px-4 py-3 text-slate-600">{kpi.measurement}</td>
                                      <td className="px-4 py-3 text-slate-600">{kpi.timeline}</td>
                                      <td className="px-4 py-3 text-right font-medium text-slate-700">{kpi.weight}%</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            
                            {/* Legacy goal-level comment, shown read-only for reviews from before per-KPI comments */}
                            {(typeof memberReview?.mid_year_feedback?.employee_feedback?.[goal.id] === 'string' ||
                              typeof memberReview?.mid_year_feedback?.manager_feedback?.[goal.id] === 'string') && (
                              <div className="px-4 py-2 border-t bg-amber-50/40 text-xs text-muted-foreground italic">
                                Previous comment (submitted before per-KPI comments):
                                {typeof memberReview?.mid_year_feedback?.employee_feedback?.[goal.id] === 'string' && (
                                  <div className="mt-1"><span className="font-medium not-italic">Employee:</span> {memberReview.mid_year_feedback.employee_feedback[goal.id]}</div>
                                )}
                                {typeof memberReview?.mid_year_feedback?.manager_feedback?.[goal.id] === 'string' && (
                                  <div className="mt-1"><span className="font-medium not-italic">Manager:</span> {memberReview.mid_year_feedback.manager_feedback[goal.id]}</div>
                                )}
                              </div>
                            )}

                            {goal.kpis?.map((kpi, index) => {
                              const employeeComment = memberReview?.mid_year_feedback?.employee_feedback?.[kpi.id];
                              const managerComment = memberReview?.mid_year_feedback?.manager_feedback?.[kpi.id];
                              // A manager review is FINAL (read-only) only when explicitly submitted.
                              // A saved draft (manager_submission_status === 'draft') stays editable so the manager can resume.
                              // Legacy manager feedback without a status is treated as final for backward compatibility.
                              const hasManagerFeedback = memberReview?.mid_year_feedback?.manager_feedback &&
                                Object.keys(memberReview.mid_year_feedback.manager_feedback).length > 0;
                              const isManagerFinal = hasManagerFeedback &&
                                memberReview?.mid_year_feedback?.manager_submission_status !== 'draft';
                              // Legacy goal-level comments unblock the manager box for reviews from before per-KPI comments.
                              // A rejected submission reverts to submission_status "draft" but keeps its employee_feedback
                              // content, so the manager box must also require an explicit "submitted" status — otherwise
                              // the manager could keep commenting against comments the employee hasn't finally resubmitted.
                              const employeeSubmittedFinal = memberReview?.mid_year_feedback?.submission_status === 'submitted';
                              const employeeHasResponded = employeeSubmittedFinal && (Boolean(employeeComment) ||
                                typeof memberReview?.mid_year_feedback?.employee_feedback?.[goal.id] === 'string');
                              return (
                                <div key={kpi.id || index} className="border-t">
                                  <div className="px-4 pt-2 text-xs font-semibold text-slate-600">{kpi.name}</div>
                                  {/* Employee Comment */}
                                  <div className="px-4 py-3 bg-blue-50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Users className="h-3.5 w-3.5 text-blue-600" />
                                      <span className="text-xs font-medium text-blue-700">Employee Comment</span>
                                      {employeeComment ? (
                                        <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
                                      ) : (
                                        <Badge className="bg-gray-100 text-gray-500 text-xs ml-auto">Pending</Badge>
                                      )}
                                    </div>
                                    <div className="text-sm text-slate-700 bg-white p-2 rounded border">
                                      {employeeComment ||
                                        <span className="text-muted-foreground italic">No comment submitted yet</span>
                                      }
                                    </div>
                                  </div>

                                  {/* Manager Comment */}
                                  <div className="px-4 py-3 bg-emerald-50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <UserCheck className="h-3.5 w-3.5 text-emerald-600" />
                                      <span className="text-xs font-medium text-emerald-700">Manager Comment</span>
                                      {isManagerFinal ? (
                                        <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
                                      ) : !employeeHasResponded ? (
                                        <Badge className="bg-gray-100 text-gray-500 text-xs ml-auto">Waiting for Employee</Badge>
                                      ) : midYearManagerComments[kpi.id]?.trim() ? (
                                        <Badge className="bg-yellow-100 text-yellow-700 text-xs ml-auto">Draft</Badge>
                                      ) : (
                                        <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">Your Turn</Badge>
                                      )}
                                    </div>
                                    {isManagerFinal ? (
                                      <div className="text-sm text-slate-700 bg-white p-2 rounded border">
                                        {managerComment}
                                      </div>
                                    ) : employeeHasResponded ? (
                                      <Textarea
                                        placeholder={`Add your feedback for "${kpi.name}"...`}
                                        value={midYearManagerComments[kpi.id] || ''}
                                        onChange={(e) => setMidYearManagerComments(prev => ({ ...prev, [kpi.id]: e.target.value }))}
                                        rows={2}
                                        className="text-sm bg-white"
                                      />
                                    ) : (
                                      <div className="text-sm text-muted-foreground italic bg-white p-2 rounded border">
                                        Employee must submit their comment first
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      
                      {/* Save Draft + Submit — shown unless the manager review is FINALLY submitted,
                          and only once the employee has submitted their comments. */}
                      {(() => {
                        const myf = memberReview?.mid_year_feedback;
                        const hasMgr = myf?.manager_feedback && Object.keys(myf.manager_feedback).length > 0;
                        const managerFinal = hasMgr && myf?.manager_submission_status !== 'draft';
                        // A rejected submission reverts to submission_status "draft" but keeps its
                        // employee_feedback content — require the employee to have finally
                        // resubmitted before the manager can act on it again.
                        const employeeReady = myf?.employee_feedback && myf?.submission_status === 'submitted' &&
                          memberGoals.filter(g => g.status === 'approved').every(g =>
                            (g.kpis?.every(kpi => myf?.employee_feedback?.[kpi.id]) ?? true) ||
                            typeof myf?.employee_feedback?.[g.id] === 'string'
                          );
                        if (managerFinal || !employeeReady) return null;
                        const allFilled = hasAllKpiComments(memberGoals, midYearManagerComments);
                        const anyFilled = Object.values(midYearManagerComments).some(v => v?.trim());
                        return (
                          <div className="flex justify-between items-center pt-3 gap-3">
                            <Button
                              variant="outline"
                              onClick={handleSaveMidYearDraft}
                              disabled={submitting || !anyFilled}
                              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            >
                              <Save className="h-4 w-4 mr-2" />
                              Save Draft (finish later)
                            </Button>
                            <Button
                              onClick={handleSubmitMidYearFeedback}
                              disabled={submitting || !allFilled}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                              Submit All Comments
                            </Button>
                          </div>
                        );
                      })()}

                      {/* Reject Employee Comments — mirrors the goal Reject All flow: only offered
                          once the employee has FINALLY submitted (submission_status === 'submitted')
                          and the manager hasn't already finalized their own review. */}
                      {(() => {
                        const myf = memberReview?.mid_year_feedback;
                        const hasMgr = myf?.manager_feedback && Object.keys(myf.manager_feedback).length > 0;
                        const managerFinal = hasMgr && myf?.manager_submission_status !== 'draft';
                        const employeeSubmittedFinal = myf?.submission_status === 'submitted';
                        if (managerFinal || !employeeSubmittedFinal) return null;
                        return (
                          <div className="mt-3">
                            {!showCommentRejectForm || showCommentRejectForm !== 'mid_year' ? (
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  onClick={() => setShowCommentRejectForm('mid_year')}
                                  disabled={submitting}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Reject Comments
                                </Button>
                              </div>
                            ) : (
                              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                <Label className="text-red-700 font-medium">Rejection Feedback (Required)</Label>
                                <p className="text-xs text-red-600 mb-2">
                                  This feedback will be sent to the employee. They will need to revise and resubmit their comments.
                                </p>
                                <Textarea
                                  value={commentRejectionFeedback}
                                  onChange={(e) => setCommentRejectionFeedback(e.target.value)}
                                  placeholder="Please explain what needs to be revised..."
                                  className="mt-2 border-red-200 bg-white"
                                  rows={3}
                                />
                                <div className="flex gap-2 mt-3 justify-end">
                                  <Button
                                    variant="outline"
                                    onClick={() => { setShowCommentRejectForm(null); setCommentRejectionFeedback(''); }}
                                    disabled={submitting}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    onClick={() => handleRejectComments('mid_year')}
                                    disabled={submitting || !commentRejectionFeedback.trim()}
                                  >
                                    Confirm Reject
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Mid-Year General Feedback Section */}
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                        <h5 className="font-medium text-sm flex items-center gap-2 mb-3 text-amber-700">
                          <FileText className="h-4 w-4" />
                          Mid-Year Feedback
                          {memberReview?.mid_year_feedback?.general_feedback?.what_went_well ? (
                            <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">Your Turn</Badge>
                          )}
                        </h5>
                        
                        {memberReview?.mid_year_feedback?.general_feedback?.what_went_well ? (
                          // Show submitted general feedback (read-only)
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs text-muted-foreground">What Went Well</Label>
                              <div className="text-sm bg-white p-3 rounded border mt-1">{memberReview.mid_year_feedback.general_feedback.what_went_well}</div>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">What Could Have Done Better</Label>
                              <div className="text-sm bg-white p-3 rounded border mt-1">{memberReview.mid_year_feedback.general_feedback.what_could_be_better}</div>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Employee Strengths</Label>
                              <div className="text-sm bg-white p-3 rounded border mt-1">{memberReview.mid_year_feedback.general_feedback.employee_strengths}</div>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Employee Opportunities</Label>
                              <div className="text-sm bg-white p-3 rounded border mt-1">{memberReview.mid_year_feedback.general_feedback.employee_opportunities}</div>
                            </div>
                          </div>
                        ) : (
                          // Show editable form
                          <>
                            <div className="space-y-3">
                              <div>
                                <Label className="text-xs">What Went Well</Label>
                                <Textarea
                                  placeholder="Describe what went well this period..."
                                  value={midYearFeedback.what_went_well}
                                  onChange={(e) => setMidYearFeedback(prev => ({ ...prev, what_went_well: e.target.value }))}
                                  rows={2}
                                  className="text-sm bg-white mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">What Could Have Done Better</Label>
                                <Textarea
                                  placeholder="Areas that could have been improved..."
                                  value={midYearFeedback.what_could_be_better}
                                  onChange={(e) => setMidYearFeedback(prev => ({ ...prev, what_could_be_better: e.target.value }))}
                                  rows={2}
                                  className="text-sm bg-white mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Employee Strengths</Label>
                                <Textarea
                                  placeholder="Key strengths demonstrated..."
                                  value={midYearFeedback.employee_strengths}
                                  onChange={(e) => setMidYearFeedback(prev => ({ ...prev, employee_strengths: e.target.value }))}
                                  rows={2}
                                  className="text-sm bg-white mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Employee Opportunities</Label>
                                <Textarea
                                  placeholder="Growth opportunities and development areas..."
                                  value={midYearFeedback.employee_opportunities}
                                  onChange={(e) => setMidYearFeedback(prev => ({ ...prev, employee_opportunities: e.target.value }))}
                                  rows={2}
                                  className="text-sm bg-white mt-1"
                                />
                              </div>
                            </div>
                            {(() => {
                              // Mirrors the backend's kpi_comments_complete gate (submit_manager_general_feedback):
                              // general feedback can't be submitted until manager KPI comments are finalized.
                              const mgrFeedback = memberReview?.mid_year_feedback?.manager_feedback;
                              const kpiCommentsComplete = Boolean(mgrFeedback) &&
                                Object.keys(mgrFeedback).length > 0 &&
                                memberReview?.mid_year_feedback?.manager_submission_status !== 'draft';
                              return (
                                <div className="flex flex-col items-end mt-4 pt-3 border-t border-amber-200 gap-1">
                                  {!kpiCommentsComplete && (
                                    <p className="text-xs text-amber-700">
                                      Submit all KPI comments above before submitting this feedback.
                                    </p>
                                  )}
                                  <Button
                                    onClick={handleSubmitMidYearGeneralFeedback}
                                    disabled={submitting || !kpiCommentsComplete || !midYearFeedback.what_went_well?.trim() || !midYearFeedback.what_could_be_better?.trim() || !midYearFeedback.employee_strengths?.trim() || !midYearFeedback.employee_opportunities?.trim()}
                                    size="sm"
                                    className="bg-amber-600 hover:bg-amber-700"
                                  >
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                                    Submit Mid-Year Feedback
                                  </Button>
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Year-End Review Tab */}
            <TabsContent value="year-end-review">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-purple-600" />
                    Year-End Review
                  </CardTitle>
                  <CardDescription>
                    Review goals and rate each KPI on performance
                  </CardDescription>
                  {/* Progress Tracker for Year-End Review */}
                  {memberGoals.filter(g => g.status === 'approved').length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <SectionProgressTracker 
                        sections={[
                          { 
                            name: 'Goal Comments',
                            completed: memberReview?.year_end_feedback?.manager_feedback &&
                              Object.keys(memberReview.year_end_feedback.manager_feedback).length > 0 &&
                              memberGoals.filter(g => g.status === 'approved').every(g => g.kpis?.every(kpi =>
                                memberReview?.year_end_feedback?.manager_feedback?.[kpi.id]
                              ) ?? true)
                          },
                          { 
                            name: 'KPI Ratings', 
                            completed: memberGoals.filter(g => g.status === 'approved').every(g => g.kpis?.every(kpi => kpi.rating))
                          },
                          { 
                            name: 'Career Development', 
                            completed: !!memberReview?.career_development 
                          },
                          { 
                            name: 'Competency Ratings', 
                            completed: memberReview?.competency_ratings?.length > 0 
                          },
                        ]}
                      />
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {memberGoals.filter(g => g.status === 'approved').length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      No approved goals to review yet
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Goals with Employee Comments, Manager Comments & KPI Ratings Combined */}
                      <div className="space-y-4">
                        {memberGoals.filter(g => g.status === 'approved').map((goal) => (
                          <div key={goal.id} className="border rounded-lg bg-white overflow-hidden">
                            {/* Goal Header */}
                            <div className="bg-slate-50 px-4 py-3 border-b">
                              <div className="flex items-center gap-2">
                                {goal.goal_type === 'business' ? 
                                  <Briefcase className="h-4 w-4 text-blue-600" /> : 
                                  <Users className="h-4 w-4 text-purple-600" />
                                }
                                <span className="font-medium">{goal.title}</span>
                                <Badge variant="outline" className="text-xs ml-auto">{goal.weight}%</Badge>
                                <Badge variant="outline" className="text-xs">
                                  {goal.goal_type === 'business' ? 'Business' : 'People'}
                                </Badge>
                              </div>
                            </div>
                            
                            {/* KPIs Table with Ratings */}
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-gray-50 border-b">
                                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">KPI</th>
                                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Measurement</th>
                                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Deadline</th>
                                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Weight</th>
                                    <th className="text-center px-4 py-2 font-medium text-muted-foreground">Rating</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {goal.kpis?.map((kpi, index) => {
                                    const legacyManagerSubmitted = typeof memberReview?.year_end_feedback?.manager_feedback?.[goal.id] === 'string';
                                    const legacyEmployeeSubmitted = typeof memberReview?.year_end_feedback?.employee_feedback?.[goal.id] === 'string';
                                    const managerSubmittedForKpi = Boolean(memberReview?.year_end_feedback?.manager_feedback?.[kpi.id]) || legacyManagerSubmitted;
                                    // A rejected submission reverts to submission_status "draft" but keeps its
                                    // employee_feedback content, so require an explicit "submitted" status before
                                    // the manager can rate against it.
                                    const employeeSubmittedForKpi = memberReview?.year_end_feedback?.submission_status === 'submitted' &&
                                      (Boolean(memberReview?.year_end_feedback?.employee_feedback?.[kpi.id]) || legacyEmployeeSubmitted);
                                    return (
                                    <tr key={kpi.id || index} className="border-b last:border-0 hover:bg-gray-50/50">
                                      <td className="px-4 py-3 text-slate-800">{kpi.name}</td>
                                      <td className="px-4 py-3 text-slate-600">{kpi.measurement}</td>
                                      <td className="px-4 py-3 text-slate-600">{kpi.timeline}</td>
                                      <td className="px-4 py-3 text-right font-medium text-slate-700">{kpi.weight}%</td>
                                      <td className="px-4 py-3 text-center">
                                        {managerSubmittedForKpi ? (
                                          <Badge className={`text-xs ${
                                            kpi.rating >= 4 ? 'bg-green-100 text-green-700' :
                                            kpi.rating === 3 ? 'bg-blue-100 text-blue-700' :
                                            kpi.rating >= 1 ? 'bg-amber-100 text-amber-700' :
                                            'bg-gray-100 text-gray-500'
                                          }`}>
                                            {kpi.rating ? `${kpi.rating}/5` : '-'}
                                          </Badge>
                                        ) : employeeSubmittedForKpi ? (
                                          <SearchableSelect
                                            value={kpiRatings?.[kpi.id]?.toString() || ''}
                                            onValueChange={(value) => setKpiRatings(prev => ({ ...prev, [kpi.id]: parseInt(value) }))}
                                            placeholder="Rate..."
                                            className="w-28 h-8 text-xs mx-auto"
                                            options={[
                                              { value: "1", label: "1 - Poor" },
                                              { value: "2", label: "2 - Below" },
                                              { value: "3", label: "3 - Meets" },
                                              { value: "4", label: "4 - Exceeds" },
                                              { value: "5", label: "5 - Outstanding" },
                                            ]}
                                          />
                                        ) : (
                                          <span className="text-xs text-muted-foreground">-</span>
                                        )}
                                      </td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            {/* Legacy goal-level comment, shown read-only for reviews from before per-KPI comments */}
                            {(typeof memberReview?.year_end_feedback?.employee_feedback?.[goal.id] === 'string' ||
                              typeof memberReview?.year_end_feedback?.manager_feedback?.[goal.id] === 'string') && (
                              <div className="px-4 py-2 border-t bg-purple-50/40 text-xs text-muted-foreground italic">
                                Previous comment (submitted before per-KPI comments):
                                {typeof memberReview?.year_end_feedback?.employee_feedback?.[goal.id] === 'string' && (
                                  <div className="mt-1"><span className="font-medium not-italic">Employee:</span> {memberReview.year_end_feedback.employee_feedback[goal.id]}</div>
                                )}
                                {typeof memberReview?.year_end_feedback?.manager_feedback?.[goal.id] === 'string' && (
                                  <div className="mt-1"><span className="font-medium not-italic">Manager:</span> {memberReview.year_end_feedback.manager_feedback[goal.id]}</div>
                                )}
                              </div>
                            )}

                            {goal.kpis?.map((kpi, index) => {
                              const employeeComment = memberReview?.year_end_feedback?.employee_feedback?.[kpi.id];
                              const managerComment = memberReview?.year_end_feedback?.manager_feedback?.[kpi.id];
                              // See the "employeeSubmittedForKpi" comment above — require an explicit "submitted" status.
                              const employeeSubmittedFinal = memberReview?.year_end_feedback?.submission_status === 'submitted';
                              const employeeHasResponded = employeeSubmittedFinal && (Boolean(employeeComment) ||
                                typeof memberReview?.year_end_feedback?.employee_feedback?.[goal.id] === 'string');
                              return (
                                <div key={kpi.id || index} className="border-t">
                                  <div className="px-4 pt-2 text-xs font-semibold text-slate-600">{kpi.name}</div>
                                  {/* Employee Comment */}
                                  <div className="px-4 py-3 bg-blue-50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Users className="h-3.5 w-3.5 text-blue-600" />
                                      <span className="text-xs font-medium text-blue-700">Employee Comment</span>
                                      {employeeComment ? (
                                        <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
                                      ) : (
                                        <Badge className="bg-gray-100 text-gray-500 text-xs ml-auto">Pending</Badge>
                                      )}
                                    </div>
                                    <div className="text-sm text-slate-700 bg-white p-2 rounded border">
                                      {employeeComment ||
                                        <span className="text-muted-foreground italic">No comment submitted yet</span>
                                      }
                                    </div>
                                  </div>

                                  {/* Manager Comment */}
                                  <div className="px-4 py-3 bg-purple-50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Star className="h-3.5 w-3.5 text-purple-600" />
                                      <span className="text-xs font-medium text-purple-700">Manager Comment</span>
                                      {managerComment ? (
                                        <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
                                      ) : employeeHasResponded ? (
                                        <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">Your Turn</Badge>
                                      ) : (
                                        <Badge className="bg-gray-100 text-gray-500 text-xs ml-auto">Waiting for Employee</Badge>
                                      )}
                                    </div>
                                    {managerComment ? (
                                      <div className="text-sm text-slate-700 bg-white p-2 rounded border">
                                        {managerComment}
                                      </div>
                                    ) : employeeHasResponded ? (
                                      <Textarea
                                        placeholder={`Add your year-end feedback for "${kpi.name}"...`}
                                        value={yearEndManagerComments[kpi.id] || ''}
                                        onChange={(e) => setYearEndManagerComments(prev => ({ ...prev, [kpi.id]: e.target.value }))}
                                        rows={2}
                                        className="text-sm bg-white"
                                      />
                                    ) : (
                                      <div className="text-sm text-muted-foreground italic bg-white p-2 rounded border">
                                        Employee must submit their comment first
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      
                      {/* Submit Button - only show if manager hasn't submitted AND employee has finally submitted all comments
                          (a rejected submission reverts to submission_status "draft" but keeps its employee_feedback
                          content, so require an explicit "submitted" status before the manager can act again). */}
                      {(!memberReview?.year_end_feedback?.manager_feedback || Object.keys(memberReview.year_end_feedback.manager_feedback).length === 0) &&
                       memberReview?.year_end_feedback?.employee_feedback &&
                       memberReview?.year_end_feedback?.submission_status === 'submitted' &&
                       memberGoals.filter(g => g.status === 'approved').every(g =>
                         (g.kpis?.every(kpi => memberReview?.year_end_feedback?.employee_feedback?.[kpi.id]) ?? true) ||
                         typeof memberReview?.year_end_feedback?.employee_feedback?.[g.id] === 'string'
                       ) ? (
                        <div className="flex justify-end pt-3">
                          <Button
                            onClick={handleSubmitYearEndFeedback}
                            disabled={submitting ||
                              !hasAllKpiComments(memberGoals, yearEndManagerComments) ||
                              !hasAllKpiRatings(memberGoals, kpiRatings)
                            }
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                            Submit All Comments & Ratings
                          </Button>
                        </div>
                      ) : null}

                      {/* Reject Employee Comments — mirrors the mid-year reject block above: only offered
                          once the employee has FINALLY submitted (submission_status === 'submitted')
                          and the manager hasn't already finalized their own review. */}
                      {(() => {
                        const yef = memberReview?.year_end_feedback;
                        const hasMgr = yef?.manager_feedback && Object.keys(yef.manager_feedback).length > 0;
                        const managerFinal = hasMgr && yef?.manager_submission_status !== 'draft';
                        const employeeSubmittedFinal = yef?.submission_status === 'submitted';
                        if (managerFinal || !employeeSubmittedFinal) return null;
                        return (
                          <div className="mt-3">
                            {!showCommentRejectForm || showCommentRejectForm !== 'year_end' ? (
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  onClick={() => setShowCommentRejectForm('year_end')}
                                  disabled={submitting}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Reject Comments
                                </Button>
                              </div>
                            ) : (
                              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                <Label className="text-red-700 font-medium">Rejection Feedback (Required)</Label>
                                <p className="text-xs text-red-600 mb-2">
                                  This feedback will be sent to the employee. They will need to revise and resubmit their comments.
                                </p>
                                <Textarea
                                  value={commentRejectionFeedback}
                                  onChange={(e) => setCommentRejectionFeedback(e.target.value)}
                                  placeholder="Please explain what needs to be revised..."
                                  className="mt-2 border-red-200 bg-white"
                                  rows={3}
                                />
                                <div className="flex gap-2 mt-3 justify-end">
                                  <Button
                                    variant="outline"
                                    onClick={() => { setShowCommentRejectForm(null); setCommentRejectionFeedback(''); }}
                                    disabled={submitting}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    onClick={() => handleRejectComments('year_end')}
                                    disabled={submitting || !commentRejectionFeedback.trim()}
                                  >
                                    Confirm Reject
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Career Development Section */}
                      <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                        <h5 className="font-medium text-sm flex items-center gap-2 mb-3 text-indigo-700">
                          <TrendingUp className="h-4 w-4" />
                          Career Development
                          {memberReview?.career_development ? (
                            <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">Your Turn</Badge>
                          )}
                        </h5>
                        
                        {memberReview?.career_development ? (
                          // Show submitted career development (read-only)
                          <div className="text-sm bg-white p-3 rounded border">{memberReview.career_development}</div>
                        ) : (
                          // Show editable form
                          <>
                            <Textarea
                              placeholder="Describe career development plans and employee development needs..."
                              value={careerDevelopment}
                              onChange={(e) => setCareerDevelopment(e.target.value)}
                              rows={4}
                              className="text-sm bg-white"
                            />
                            <div className="flex justify-end mt-4 pt-3 border-t border-indigo-200">
                              <Button 
                                onClick={handleSubmitCareerDevelopment} 
                                disabled={submitting || !careerDevelopment?.trim()}
                                size="sm"
                                className="bg-indigo-600 hover:bg-indigo-700"
                              >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                                Submit Career Development
                              </Button>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Competency Ratings Section - Moved from separate tab */}
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 mt-4">
                        <h5 className="font-medium text-sm flex items-center gap-2 mb-4 text-amber-700">
                          <Star className="h-4 w-4" />
                          Competency Ratings
                          {memberReview?.competency_ratings?.length > 0 ? (
                            <Badge className="bg-green-100 text-green-700 text-xs ml-auto">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Submitted
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">Pending</Badge>
                          )}
                        </h5>
                        
                        {memberReview?.competency_ratings?.length > 0 ? (
                          // Show submitted ratings (read-only)
                          <div className="space-y-3">
                            <div className="text-sm mb-3">
                              <span className="text-muted-foreground">Competency Score: </span>
                              <span className="font-bold">{memberReview.competency_score?.toFixed(1) || '-'}/5</span>
                            </div>
                            {memberReview.competency_ratings.map((cr, idx) => (
                              <div key={idx} className="bg-white rounded-lg p-3 border">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm">{cr.competency_name}</span>
                                  <Badge className={
                                    cr.rating >= 4 ? 'bg-green-100 text-green-700' :
                                    cr.rating === 3 ? 'bg-blue-100 text-blue-700' :
                                    'bg-amber-100 text-amber-700'
                                  }>
                                    {cr.rating}/5
                                  </Badge>
                                </div>
                                {cr.feedback && (
                                  <p className="text-xs text-muted-foreground mt-1">{cr.feedback}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : competencies.filter(c => c.is_active).length > 0 ? (
                          // Show rating form
                          <div className="space-y-4">
                            {competencies.filter(c => c.is_active).map((comp) => (
                              <div key={comp.id} className="bg-white rounded-lg p-3 border">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <h6 className="font-medium text-sm">{comp.name}</h6>
                                    <p className="text-xs text-muted-foreground">{comp.description}</p>
                                  </div>
                                  <SearchableSelect
                                    value={competencyRatings[comp.id]?.toString() || ''}
                                    onValueChange={(value) => setCompetencyRatings(prev => ({ ...prev, [comp.id]: parseInt(value) }))}
                                    placeholder="Rate..."
                                    className="w-28 h-8 text-xs"
                                    options={[
                                      { value: "1", label: "1 - Poor" },
                                      { value: "2", label: "2 - Below" },
                                      { value: "3", label: "3 - Meets" },
                                      { value: "4", label: "4 - Exceeds" },
                                      { value: "5", label: "5 - Outstanding" },
                                    ]}
                                  />
                                </div>
                                
                                {/* Expected and Non-Display Behaviors - Collapsible */}
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  <div className="bg-green-50 rounded p-2 border border-green-200">
                                    <span className="text-xs font-medium text-green-700 flex items-center gap-1">
                                      <CheckCircle className="h-3 w-3" /> Expected
                                    </span>
                                    <ul className="text-xs mt-1 space-y-0.5">
                                      {comp.expected_behaviors?.slice(0, 2).map((b, i) => (
                                        <li key={i} className="text-green-800">• {b}</li>
                                      ))}
                                      {comp.expected_behaviors?.length > 2 && (
                                        <li className="text-green-600 italic">+{comp.expected_behaviors.length - 2} more</li>
                                      )}
                                    </ul>
                                  </div>
                                  <div className="bg-red-50 rounded p-2 border border-red-200">
                                    <span className="text-xs font-medium text-red-700 flex items-center gap-1">
                                      <XCircle className="h-3 w-3" /> Avoid
                                    </span>
                                    <ul className="text-xs mt-1 space-y-0.5">
                                      {comp.non_display_behaviors?.slice(0, 2).map((b, i) => (
                                        <li key={i} className="text-red-800">• {b}</li>
                                      ))}
                                      {comp.non_display_behaviors?.length > 2 && (
                                        <li className="text-red-600 italic">+{comp.non_display_behaviors.length - 2} more</li>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                                
                                {/* Feedback */}
                                <Textarea
                                  placeholder="Feedback (optional)..."
                                  value={competencyFeedback[comp.id] || ''}
                                  onChange={(e) => setCompetencyFeedback(prev => ({ ...prev, [comp.id]: e.target.value }))}
                                  rows={1}
                                  className="text-xs mt-2"
                                />
                              </div>
                            ))}
                            
                            {/* Submit Button */}
                            <div className="flex justify-end pt-2">
                              <Button 
                                onClick={handleSubmitCompetencyRatings} 
                                disabled={submitting || !competencies.filter(c => c.is_active).every(c => competencyRatings[c.id])}
                                size="sm"
                                className="bg-amber-600 hover:bg-amber-700"
                              >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                                Submit Competency Ratings
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No competencies configured. Please add competencies in Competency Management.
                          </p>
                        )}
                      </div>

                      {/* Performance Scores Summary */}
                      {memberReview && (
                        <div className="bg-slate-50 rounded-lg p-4 border mt-4">
                          <h5 className="font-medium text-sm mb-3">Performance Scores</h5>
                          <div className="grid grid-cols-4 gap-3">
                            <div className="bg-white p-3 rounded border text-center">
                              <p className="text-xl font-bold">{memberReview.goal_score?.toFixed(1) || '-'}</p>
                              <p className="text-xs text-muted-foreground">Goal Score</p>
                            </div>
                            <div className="bg-white p-3 rounded border text-center">
                              <p className="text-xl font-bold">{memberReview.competency_score?.toFixed(1) || '-'}</p>
                              <p className="text-xs text-muted-foreground">Competency</p>
                            </div>
                            <div className="bg-white p-3 rounded border text-center">
                              <p className="text-xl font-bold">{memberReview.overall_rating?.toFixed(1) || '-'}</p>
                              <p className="text-xs text-muted-foreground">Overall</p>
                            </div>
                            <div className="bg-primary/10 p-3 rounded border text-center">
                              <p className="text-xl font-bold text-primary">{memberReview.calibrated_rating?.toFixed(1) || '-'}</p>
                              <p className="text-xs text-muted-foreground">Calibrated</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Calibration Tab */}
            <TabsContent value="calibration">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCheck className="h-5 w-5 text-amber-500" />
                    Calibration
                  </CardTitle>
                  <CardDescription>
                    View calibration details for {selectedMember.name}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!memberReview?.goal_score ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      <p>Year-end review not yet completed.</p>
                      <p className="text-sm mt-1">Calibration will be available after the year-end review is submitted.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Performance Scores */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg text-center border border-blue-200">
                          <p className="text-2xl font-bold text-blue-700">{memberReview?.goal_score?.toFixed(1) || '-'}</p>
                          <p className="text-xs text-blue-600 font-medium">Goal Score</p>
                          <p className="text-xs text-blue-500">80% Weight</p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg text-center border border-purple-200">
                          <p className="text-2xl font-bold text-purple-700">{memberReview?.competency_score?.toFixed(1) || '-'}</p>
                          <p className="text-xs text-purple-600 font-medium">Competency</p>
                          <p className="text-xs text-purple-500">20% Weight</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg text-center border border-green-200">
                          <p className="text-2xl font-bold text-green-700">{memberReview?.overall_rating?.toFixed(1) || '-'}</p>
                          <p className="text-xs text-green-600 font-medium">Overall Rating</p>
                        </div>
                        <div className="bg-amber-50 p-4 rounded-lg text-center border border-amber-200">
                          <p className="text-2xl font-bold text-amber-700">{memberReview?.calibrated_rating?.toFixed(1) || '-'}</p>
                          <p className="text-xs text-amber-600 font-medium">Calibrated Rating</p>
                          <p className="text-xs text-amber-500">{memberReview?.calibrated_rating ? 'Set by HR' : 'Pending HR'}</p>
                        </div>
                      </div>
                      
                      {/* HR Notes */}
                      {memberReview?.hr_notes && (
                        <div className="bg-slate-50 rounded-lg p-4 border">
                          <h4 className="text-sm font-medium mb-2">HR Notes</h4>
                          <p className="text-sm text-muted-foreground">{memberReview.hr_notes}</p>
                        </div>
                      )}
                      
                      {/* Signatures Section */}
                      <div className="border rounded-lg p-4">
                        <h4 className="text-sm font-medium mb-4">Signatures</h4>
                        <div className="grid grid-cols-2 gap-4">
                          {/* Manager Signature */}
                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Manager Signature</Label>
                            {memberReview?.manager_signature ? (
                              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                                <CheckCircle className="h-4 w-4" />
                                <span className="text-sm font-medium">{memberReview.manager_signature}</span>
                              </div>
                            ) : signingAs === 'manager' ? (
                              <div className="space-y-2">
                                <Input
                                  placeholder="Enter your full name to sign"
                                  value={signatureName}
                                  onChange={(e) => setSignatureName(e.target.value)}
                                />
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={handleSignCalibration} disabled={signingLoading || !signatureName.trim()}>
                                    {signingLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PenTool className="h-4 w-4 mr-1" />}
                                    Confirm
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => { setSigningAs(null); setSignatureName(''); }}>
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : memberReview?.calibrated_rating ? (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="w-full"
                                onClick={() => setSigningAs('manager')}
                              >
                                <PenTool className="h-4 w-4 mr-1" />
                                Sign as Manager
                              </Button>
                            ) : (
                              <div className="text-sm text-muted-foreground bg-gray-50 p-3 rounded-lg">
                                Available after HR calibration
                              </div>
                            )}
                          </div>
                          
                          {/* Employee Signature */}
                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Employee Signature</Label>
                            {memberReview?.employee_signature ? (
                              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                                <CheckCircle className="h-4 w-4" />
                                <span className="text-sm font-medium">{memberReview.employee_signature}</span>
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground bg-gray-50 p-3 rounded-lg">
                                Pending employee signature
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Status */}
                      <div className="flex items-center gap-2 pt-4 border-t">
                        <span className="text-sm text-muted-foreground">Status:</span>
                        <Badge variant={memberReview?.status === 'completed' ? 'default' : 'outline'}>
                          {memberReview?.status === 'completed' ? 'Completed' : 'In Progress'}
                        </Badge>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    );
  }

  // Overview View - Team Dashboard
  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-goals-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Team Performance Overview</h1>
        <p className="text-muted-foreground mt-1">
          View your team&apos;s progress across all performance cycle stages for {currentYear}
        </p>
      </div>

      {/* Legend */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm text-muted-foreground font-medium">Status Legend:</span>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-gray-400" />
              <span className="text-sm">Not Due</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-sm">Pending Submission</span>
            </div>
            <div className="flex items-center gap-2">
              <Send className="h-4 w-4 text-blue-500" />
              <span className="text-sm">Pending Manager</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              <span className="text-sm">Completed</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Team Status Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Members ({teamStatus.length})
          </CardTitle>
          <CardDescription>
            Click on any row to view detailed goals and reviews
          </CardDescription>
        </CardHeader>
        <CardContent>
          {teamStatus.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No team members found</h3>
              <p className="text-muted-foreground">
                {isAdmin || isHR ? 'No employees in the system' : 'You have no direct reports'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="text-center">
                    <div className="flex flex-col items-center">
                      <Target className="h-4 w-4 mb-1" />
                      <span>Goal Setting</span>
                      <span className="text-xs text-muted-foreground">
                        ({formatStageDate(performanceCycle?.goal_setting_start) || 'Jan 1'})
                      </span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex flex-col items-center">
                      <FileText className="h-4 w-4 mb-1" />
                      <span>Mid-Year Review</span>
                      <span className="text-xs text-muted-foreground">
                        ({formatStageDate(performanceCycle?.mid_year_start) || 'Jul 1'})
                      </span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex flex-col items-center">
                      <Award className="h-4 w-4 mb-1" />
                      <span>Year-End Review</span>
                      <span className="text-xs text-muted-foreground">
                        ({formatStageDate(performanceCycle?.year_end_start) || 'Jan 1'})
                      </span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex flex-col items-center">
                      <UserCheck className="h-4 w-4 mb-1" />
                      <span>Calibration</span>
                      <span className="text-xs text-muted-foreground">
                        ({formatStageDate(performanceCycle?.calibration_start) || 'Feb 1'})
                      </span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex flex-col items-center">
                      <BarChart3 className="h-4 w-4 mb-1" />
                      <span>Scorecard</span>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamStatus.map((member) => (
                  <TableRow 
                    key={member.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => loadMemberDetails(member)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {member.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-xs text-muted-foreground">{member.employee_number}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{member.department || '-'}</p>
                        <p className="text-xs text-muted-foreground">{member.designation || '-'}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <ReviewStageStatusBadge status={member.stages.goal_setting} />
                    </TableCell>
                    <TableCell className="text-center">
                      <ReviewStageStatusBadge status={member.stages.mid_year} />
                    </TableCell>
                    <TableCell className="text-center">
                      <ReviewStageStatusBadge status={member.stages.year_end} />
                    </TableCell>
                    <TableCell className="text-center">
                      <CalibrationStatusBadge status={member.stages.calibration} />
                    </TableCell>
                    <TableCell className="text-center">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => handleViewScorecard(member, e)}
                        className="h-7 px-2"
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Scorecard Modal */}
      <Dialog open={showScorecardModal} onOpenChange={setShowScorecardModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Award className="h-6 w-6 text-primary" />
              Performance Scorecard
            </DialogTitle>
            <DialogDescription>
              {scorecardMember?.name || 'Employee'} • Year {currentYear} Performance Review
            </DialogDescription>
          </DialogHeader>
          
          {loadingScorecard ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Scores Overview */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center border border-blue-200">
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Goal Score</p>
                  <p className="text-2xl font-bold text-blue-900">{scorecardReview?.goal_score?.toFixed(1) || '-'}</p>
                  <p className="text-xs text-blue-600 mt-1">80% Weight</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center border border-purple-200">
                  <p className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-1">Competency</p>
                  <p className="text-2xl font-bold text-purple-900">{scorecardReview?.competency_score?.toFixed(1) || '-'}</p>
                  <p className="text-xs text-purple-600 mt-1">20% Weight</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 text-center border border-emerald-200">
                  <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Overall</p>
                  <p className="text-2xl font-bold text-emerald-900">{scorecardReview?.overall_rating?.toFixed(1) || '-'}</p>
                  <Badge className={`mt-1 text-xs ${getRatingColor(scorecardReview?.overall_rating)}`}>
                    {getRatingLabel(scorecardReview?.overall_rating)}
                  </Badge>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 text-center border border-amber-200">
                  <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-1">Calibrated</p>
                  <p className="text-2xl font-bold text-amber-900">{scorecardReview?.calibrated_rating?.toFixed(1) || '-'}</p>
                  {scorecardReview?.calibrated_rating ? (
                    <Badge className={`mt-1 text-xs ${getRatingColor(scorecardReview?.calibrated_rating)}`}>
                      {getRatingLabel(scorecardReview?.calibrated_rating)}
                    </Badge>
                  ) : (
                    <p className="text-xs text-amber-600 mt-1">Pending HR</p>
                  )}
                </div>
              </div>
              
              {/* Goals Breakdown */}
              <div className="bg-slate-50 rounded-xl p-4 border">
                <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                  <Target className="h-4 w-4 text-blue-600" />
                  Goals & KPI Performance
                </h4>
                <div className="space-y-3">
                  {scorecardGoals?.filter(g => g.status === 'approved').length > 0 ? (
                    scorecardGoals.filter(g => g.status === 'approved').map((goal) => (
                      <div key={goal.id} className="bg-white rounded-lg p-3 border">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {goal.goal_type === 'business' ? 
                              <Briefcase className="h-4 w-4 text-blue-600" /> : 
                              <Users className="h-4 w-4 text-purple-600" />
                            }
                            <span className="font-medium text-sm">{goal.title}</span>
                            <Badge variant="outline" className="text-xs">{goal.weight}%</Badge>
                          </div>
                          <Badge className={`${
                            goal.rating >= 4 ? 'bg-green-100 text-green-700' :
                            goal.rating >= 3 ? 'bg-blue-100 text-blue-700' :
                            goal.rating >= 1 ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {goal.rating?.toFixed(1) || '-'}/5
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {goal.kpis?.length || 0} KPIs • {goal.goal_type === 'business' ? 'Business Goal' : 'People Goal'}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No approved goals found
                    </div>
                  )}
                </div>
              </div>
              
              {/* Competency Ratings Section */}
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                  <Star className="h-4 w-4 text-purple-600" />
                  Competency Ratings
                  {scorecardReview?.competency_score && (
                    <Badge className="ml-auto bg-purple-100 text-purple-700">
                      Avg: {scorecardReview.competency_score.toFixed(1)}/5
                    </Badge>
                  )}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {scorecardReview?.competency_ratings?.length > 0 ? (
                    scorecardReview.competency_ratings.map((comp, index) => (
                      <div key={index} className="bg-white rounded-lg p-3 border">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{comp.competency_name || `Competency ${index + 1}`}</span>
                          <Badge className={`${
                            comp.rating >= 4 ? 'bg-green-100 text-green-700' :
                            comp.rating === 3 ? 'bg-blue-100 text-blue-700' :
                            comp.rating >= 1 ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {comp.rating}/5
                          </Badge>
                        </div>
                        {comp.feedback && (
                          <p className="text-xs text-muted-foreground mt-1 truncate">{comp.feedback}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="col-span-2 text-center py-4 text-muted-foreground text-sm">
                      No competency ratings submitted yet
                    </div>
                  )}
                </div>
              </div>
              
              {/* Rating Scale Reference */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4 border">
                <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                  <BarChart3 className="h-4 w-4 text-slate-600" />
                  Rating Scale Reference
                </h4>
                <div className="grid grid-cols-5 gap-2 text-center text-xs">
                  <div className="bg-teal-100 text-teal-800 rounded-lg p-2">
                    <div className="font-bold">5</div>
                    <div>Outstanding</div>
                    <div className="text-teal-600">(4.6 - 5.0)</div>
                  </div>
                  <div className="bg-green-100 text-green-800 rounded-lg p-2">
                    <div className="font-bold">4</div>
                    <div>Exceeds</div>
                    <div className="text-green-600">(3.8 - 4.5)</div>
                  </div>
                  <div className="bg-blue-100 text-blue-800 rounded-lg p-2">
                    <div className="font-bold">3</div>
                    <div>Meets</div>
                    <div className="text-blue-600">(2.7 - 3.7)</div>
                  </div>
                  <div className="bg-orange-100 text-orange-800 rounded-lg p-2">
                    <div className="font-bold">2</div>
                    <div>Partial</div>
                    <div className="text-orange-600">(1.7 - 2.6)</div>
                  </div>
                  <div className="bg-red-100 text-red-800 rounded-lg p-2">
                    <div className="font-bold">1</div>
                    <div>Did Not Meet</div>
                    <div className="text-red-600">(1.0 - 1.6)</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowScorecardModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
